import {
    Autocomplete,
    Card,
    CardContent,
    CardHeader,
    FormControlLabel,
    Grid,
    IconButton,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { CONTRACT_STATUS, PAGINATION, SYSTEMS } from "../../../constants";
import { Controller, useForm, useWatch } from "react-hook-form";

import { BACKEND_ROUTES } from "../../../backendRoutes";
import { BookingTable } from ".";
import { BookingTableFormSkeleton } from "./BookingTableFormSkeleton";
import { FetchErrorAlert } from "../../../components/FetchErrorAlert";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import useSWR from "swr";
import { useUsers } from "../../../hooks";

export function BookingTableForm() {
    const { control, setValue } = useForm({
        defaultValues: {
            year: new Date().getFullYear(),
            company: null,
            contract: null,
            processingLead: null,
            numberDisplayed: true,
            status: null,
            system: null,
            systemScaling: false,
        },
    });

    const [
        yearWatch,
        companyWatch,
        contractWatch,
        numberDisplayedWatch,
        systemScalingWatch,
    ] = useWatch({
        name: [
            "year",
            "company",
            "contract",
            "numberDisplayed",
            "systemScaling",
        ],
        control,
    });

    const { data: companies, error: companiesFetchError } = useSWR(
        `${BACKEND_ROUTES.COMPANY}?sort=name&limit=${PAGINATION.COMPANY.LIMIT.MAX}`
    );
    const { data: contracts, error: contractsFetchError } = useSWR(
        `${BACKEND_ROUTES.CONTRACT}?sort=name&limit=${PAGINATION.CONTRACT.LIMIT.MAX}`
    );
    const { data: users, error: usersFetchError } = useUsers();

    const mergedFetchError =
        contractsFetchError ?? companiesFetchError ?? usersFetchError;

    if (mergedFetchError) return <FetchErrorAlert error={mergedFetchError} />;

    if (!contracts || !companies || !users) {
        return <BookingTableFormSkeleton />;
    }

    return (
        <>
            <Typography variant="h4" gutterBottom>
                Bookings
            </Typography>
            <Card>
                <CardHeader
                    title={
                        <Stack direction="column">
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                            >
                                <Stack direction="row" alignItems="center">
                                    <IconButton
                                        size="large"
                                        onClick={() =>
                                            setValue("year", yearWatch - 1)
                                        }
                                        disabled={yearWatch <= 2000}
                                    >
                                        <NavigateBeforeIcon
                                            fontSize="inherit"
                                            color={
                                                yearWatch <= 2000
                                                    ? ""
                                                    : "primary"
                                            }
                                        />
                                    </IconButton>
                                    <Typography variant="h5">
                                        {yearWatch}
                                    </Typography>
                                    <IconButton
                                        size="large"
                                        onClick={() =>
                                            setValue("year", yearWatch + 1)
                                        }
                                        disabled={yearWatch >= 2999}
                                    >
                                        <NavigateNextIcon
                                            fontSize="inherit"
                                            color={
                                                yearWatch >= 2999
                                                    ? ""
                                                    : "primary"
                                            }
                                        />
                                    </IconButton>
                                </Stack>
                                <Stack direction="row" alignItems="center">
                                    <Controller
                                        control={control}
                                        name="numberDisplayed"
                                        render={({
                                            field: {
                                                ref,
                                                onChange,
                                                name,
                                                ...fieldProps
                                            },
                                        }) => (
                                            <FormControlLabel
                                                {...fieldProps}
                                                id="numberDisplayed"
                                                name="numberDisplayed"
                                                control={<Switch />}
                                                label="Display numbers"
                                                labelPlacement="end"
                                                checked={numberDisplayedWatch}
                                                onChange={(event) => {
                                                    onChange(
                                                        event.target.checked
                                                    );
                                                }}
                                            />
                                        )}
                                    />
                                    <Controller
                                        control={control}
                                        name="systemScaling"
                                        render={({
                                            field: {
                                                ref,
                                                onChange,
                                                name,
                                                ...fieldProps
                                            },
                                        }) => (
                                            <FormControlLabel
                                                {...fieldProps}
                                                id="systemScaling"
                                                name="systemScaling"
                                                control={<Switch />}
                                                label="Display data per System"
                                                labelPlacement="end"
                                                checked={systemScalingWatch}
                                                onChange={(event) => {
                                                    onChange(
                                                        event.target.checked
                                                    );
                                                }}
                                            />
                                        )}
                                    />
                                </Stack>
                            </Stack>
                            <Grid container spacing={2} alignItems="center">
                                <Grid size="grow">
                                    <Controller
                                        control={control}
                                        name="company"
                                        render={({
                                            field: {
                                                ref,
                                                onChange,
                                                name,
                                                ...fieldProps
                                            },
                                        }) => (
                                            <Autocomplete
                                                {...fieldProps}
                                                id="company"
                                                options={companies.rows}
                                                isOptionEqualToValue={(
                                                    option,
                                                    value
                                                ) => option.uuid === value.uuid}
                                                getOptionLabel={(option) =>
                                                    option.name
                                                }
                                                onChange={(event, value) => {
                                                    onChange(value);
                                                    if (
                                                        value &&
                                                        contractWatch?.companyUuid !==
                                                            value.uuid
                                                    )
                                                        setValue(
                                                            "contract",
                                                            null
                                                        );
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        inputRef={ref}
                                                        name={name}
                                                        label="Filter on company"
                                                    />
                                                )}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid size="grow">
                                    <Controller
                                        control={control}
                                        name="contract"
                                        render={({
                                            field: {
                                                ref,
                                                onChange,
                                                name,
                                                ...fieldProps
                                            },
                                        }) => (
                                            <Autocomplete
                                                {...fieldProps}
                                                id="contract"
                                                options={contracts.rows.filter(
                                                    (contract) =>
                                                        companyWatch
                                                            ? companyWatch.uuid ===
                                                              contract.companyUuid
                                                            : true
                                                )}
                                                isOptionEqualToValue={(
                                                    option,
                                                    value
                                                ) => option.uuid === value.uuid}
                                                getOptionLabel={(option) =>
                                                    option.name
                                                }
                                                onChange={(event, value) => {
                                                    onChange(value);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        inputRef={ref}
                                                        name={name}
                                                        label="Filter on campaign"
                                                    />
                                                )}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid size="grow">
                                    <Controller
                                        control={control}
                                        name="processingLead"
                                        render={({
                                            field: {
                                                ref,
                                                onChange,
                                                name,
                                                ...fieldProps
                                            },
                                        }) => (
                                            <Autocomplete
                                                {...fieldProps}
                                                id="processingLead"
                                                options={users}
                                                isOptionEqualToValue={(
                                                    option,
                                                    value
                                                ) => option.uuid === value.uuid}
                                                getOptionLabel={(option) =>
                                                    `${option.firstName} ${option.lastName}`
                                                }
                                                onChange={(event, value) => {
                                                    onChange(value);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        inputRef={ref}
                                                        name={name}
                                                        label="Filter on processing lead"
                                                    />
                                                )}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid size="grow">
                                    <Controller
                                        control={control}
                                        name="status"
                                        render={({
                                            field: {
                                                ref,
                                                onChange,
                                                name,
                                                ...fieldProps
                                            },
                                        }) => (
                                            <Autocomplete
                                                {...fieldProps}
                                                id="status"
                                                options={Object.values(
                                                    CONTRACT_STATUS
                                                )}
                                                onChange={(event, value) => {
                                                    onChange(value);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        inputRef={ref}
                                                        name={name}
                                                        label="Filter by status"
                                                    />
                                                )}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid size="grow">
                                    <Controller
                                        control={control}
                                        name="system"
                                        render={({
                                            field: {
                                                ref,
                                                onChange,
                                                name,
                                                ...fieldProps
                                            },
                                        }) => (
                                            <Autocomplete
                                                {...fieldProps}
                                                id="system"
                                                options={Object.values(SYSTEMS)}
                                                onChange={(event, value) => {
                                                    onChange(value);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        inputRef={ref}
                                                        name={name}
                                                        label="Filter by system"
                                                    />
                                                )}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </Stack>
                    }
                />
                <CardContent>
                    <BookingTable control={control} />
                </CardContent>
            </Card>
        </>
    );
}
